import { useMemo } from "react";
import { useAdminDeals, useAdminProducts } from "@/hooks/useAdminStore";
import type { ArticleSection } from "@/hooks/useArticleCatalog";
import OfferCard from "@/components/base/OfferCard";
import ArticleAdSlot from "./ArticleAdSlot";
import { renderArticleRichText } from "./renderArticleRichText";

interface ArticleContentProps {
  sections: ArticleSection[];
}

export default function ArticleContent({ sections }: ArticleContentProps) {
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();

  const catalogBySlug = useMemo(
    () => new Map(Array.from(new Map([...products, ...deals].map((item) => [item.slug, item])).values()).map((item) => [item.slug, item])),
    [deals, products]
  );

  const mobileInlineAdIndex = sections.length > 0 ? 0 : -1;
  const middleBannerIndex = sections.length >= 3 ? Math.min(sections.length - 2, Math.max(1, Math.floor(sections.length * 0.45))) : -1;

  return (
    <article className="space-y-10">
      {sections.map((section, index) => {
        const visibleProducts =
          section.type === "products"
            ? (section.products ?? [])
                .map((productRef, productIndex) => {
                  const product = catalogBySlug.get(productRef.productSlug);
                  if (!product) return null;

                  return {
                    rank: productIndex + 1,
                    badge: productRef.badge,
                    highlight: productRef.highlight,
                    slug: product.slug,
                    name: product.name,
                    image: product.image,
                    price: product.price,
                    originalPrice: product.originalPrice,
                    discount: product.discount,
                    rating: product.rating,
                    reviews: product.reviews,
                    marketplace: product.marketplace,
                    affiliateLink: product.affiliateLink,
                    freeShipping: product.freeShipping,
                    fulfillment: "fulfillment" in product ? product.fulfillment : false,
                    officialStore: "officialStore" in product ? product.officialStore : false,
                    officialStoreName: "officialStoreName" in product ? product.officialStoreName : "",
                    category: product.category,
                    categorySlug: product.categorySlug,
                    subcategory: "subcategory" in product ? product.subcategory : product.category,
                    subcategorySlug: "subcategorySlug" in product ? product.subcategorySlug : product.categorySlug,
                  };
                })
                .filter(Boolean)
            : [];

        return (
          <div key={section.id} className="space-y-6">
            <div id={section.id} className="scroll-mt-24">
              {section.type === "intro" && (
                <div className="space-y-2">
                  {renderArticleRichText(section.content)}
                </div>
              )}

              {section.type === "products" && (
                <div>
                  {section.title && (
                    <h2 className="mb-2 text-xl font-normal text-gray-900 md:text-2xl">
                      {section.title}
                    </h2>
                  )}
                  <div className="mb-4 space-y-2">
                    {renderArticleRichText(section.content)}
                  </div>
                  {visibleProducts.length > 0 ? (
                    <div className="flex flex-wrap justify-center gap-4">
                      {visibleProducts.map((product) => (
                        <OfferCard
                          key={product!.slug}
                          item={{
                            slug: product!.slug,
                            image: product!.image,
                            name: product!.name,
                            price: product!.price,
                            originalPrice: product!.originalPrice,
                            discount: product!.discount,
                            marketplace: product!.marketplace,
                            freeShipping: Boolean(product!.freeShipping),
                            fulfillment: Boolean(product!.fulfillment),
                            officialStoreName: product!.officialStoreName || (product!.officialStore ? "Loja Oficial" : ""),
                            affiliateLink: product!.affiliateLink,
                            isFlash: product!.badge === "Oferta Relâmpago",
                            category: product!.category,
                            categorySlug: product!.categorySlug,
                            subcategory: product!.subcategory,
                            subcategorySlug: product!.subcategorySlug,
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-5 py-8 text-center text-sm text-gray-500">
                      Os produtos deste bloco ainda não foram cadastrados ou vinculados no painel administrativo.
                    </div>
                  )}
                </div>
              )}

              {section.type === "guide" && (
                <div>
                  {section.title && (
                    <h2 className="mb-4 text-xl font-normal text-gray-900 md:text-2xl">
                      {section.title}
                    </h2>
                  )}
                  <div className="space-y-2 rounded-2xl border border-gray-100 bg-white p-6">
                    {renderArticleRichText(section.content)}
                  </div>
                </div>
              )}

              {section.type === "comparison" && (
                <div>
                  {section.title && (
                    <h2 className="mb-2 text-xl font-normal text-gray-900 md:text-2xl">
                      {section.title}
                    </h2>
                  )}
                  <div className="mb-5 space-y-2">
                    {renderArticleRichText(section.content)}
                  </div>
                  <div className="overflow-x-auto rounded-2xl border border-gray-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <th className="px-4 py-3 text-left font-bold text-gray-900">Característica</th>
                          <th className="px-4 py-3 text-center font-bold text-gray-900">{section.comparisonColumns?.first}</th>
                          <th className="px-4 py-3 text-center font-bold text-gray-900">{section.comparisonColumns?.second}</th>
                          <th className="px-4 py-3 text-center font-bold text-gray-900">{section.comparisonColumns?.third}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.comparisonTable?.map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            className={`border-b border-gray-50 ${rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                          >
                            <td className="px-4 py-3 font-semibold text-gray-900">{row.feature}</td>
                            <td className="px-4 py-3 text-center text-gray-900">{row.first}</td>
                            <td className="px-4 py-3 text-center font-medium text-gray-900">{row.second}</td>
                            <td className="px-4 py-3 text-center font-medium text-gray-900">{row.third}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {index === mobileInlineAdIndex && (
              <ArticleAdSlot
                slotKey="articleInlineMobile"
                minHeightClassName="min-h-[250px] lg:hidden"
                scrollTriggerPercent={0.2}
              />
            )}

            {index === middleBannerIndex && (
              <ArticleAdSlot
                slotKey="articleMiddleBanner"
                minHeightClassName="min-h-[290px]"
                scrollTriggerPercent={0.45}
              />
            )}
          </div>
        );
      })}
    </article>
  );
}